import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CheckCircle,
  Copy,
  AlertCircle,
  ShieldCheck,
  ArrowLeft,
  ShoppingBag,
  MapPin,
} from 'lucide-react'
import { toast } from 'sonner'
import { CreateOrderInput } from '@buddys-choice/shared'
import { apiGet, apiPost } from '../lib/api'
import { useCart } from '../context/CartContext'
import { useAuth } from '../context/AuthContext'
import { useSiteConfig } from '../context/SiteConfigContext'
import { isGstEnabled } from '../lib/companyGst'
import { StorefrontLayout } from '../layouts/StorefrontLayout'
import type { CreateOrderInput as CreateOrderInputType } from '@buddys-choice/shared'

interface SavedAddress {
  id: string
  name: string
  shippingAddressLine1: string
  shippingAddressLine2?: string
  shippingCity: string
  shippingState: string
  shippingPincode: string
}

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { items, subtotal, taxCgst, taxSgst, total, clearCart } = useCart()
  const { user } = useAuth()
  const { config } = useSiteConfig()
  const gstOn = isGstEnabled(config)

  const [step, setStep] = useState<'shipping' | 'success'>('shipping')
  const [orderResult, setOrderResult] = useState<{
    orderNumber: string
    trackingId: string
    total: number
    isPreOrder: boolean
  } | null>(null)
  const [submitError, setSubmitError] = useState('')
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([])
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null)

  const customerPhone = user?.phone ?? ''

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateOrderInputType>({
    resolver: zodResolver(CreateOrderInput) as import('react-hook-form').Resolver<CreateOrderInputType>,
    defaultValues: {
      customerPhone,
      customerName: user?.name ?? '',
      customerEmail: user?.email ?? '',
      shippingState: 'Tamil Nadu',
      paymentMethod: 'cod',
      items: [],
    },
  })

  useEffect(() => {
    if (items.length === 0 && step !== 'success') {
      navigate({ to: '/' })
    }
  }, [items.length, step, navigate])

  useEffect(() => {
    if (customerPhone) {
      setValue('customerPhone', customerPhone)
    }
    if (user?.name) {
      setValue('customerName', user.name)
    }
    const savedEmail = typeof window !== 'undefined' ? localStorage.getItem('buddys_customer_email') : null
    if (user?.email) {
      setValue('customerEmail', user.email)
    } else if (savedEmail) {
      setValue('customerEmail', savedEmail)
    }
  }, [customerPhone, user, setValue])

  useEffect(() => {
    if (user) {
      apiGet<SavedAddress[]>('customers/addresses/my')
        .then((data) => {
          if (data && data.length > 0) {
            setSavedAddresses(data)
            selectSavedAddress(data[0]!)
          }
        })
        .catch(() => {})
    }
  }, [user])

  const selectSavedAddress = (addr: SavedAddress) => {
    setSelectedAddressId(addr.id)
    setValue('customerName', addr.name)
    setValue('shippingAddressLine1', addr.shippingAddressLine1)
    if (addr.shippingAddressLine2) setValue('shippingAddressLine2', addr.shippingAddressLine2)
    setValue('shippingCity', addr.shippingCity)
    setValue('shippingState', addr.shippingState || 'Tamil Nadu')
    setValue('shippingPincode', addr.shippingPincode)
  }

  const handleAddNewAddress = () => {
    setSelectedAddressId('new')
    setValue('shippingAddressLine1', '')
    setValue('shippingAddressLine2', '')
    setValue('shippingCity', '')
    setValue('shippingState', 'Tamil Nadu')
    setValue('shippingPincode', '')
  }

  useEffect(() => {
    setValue(
      'items',
      items.map((i) => ({
        productId: i.productId,
        variantId: i.variantId,
        quantity: i.quantity,
      })),
      { shouldValidate: true }
    )
  }, [items, setValue])

  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)

  const handleCopyTrackingId = (trackingId: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(trackingId)
        .then(() => toast.success('Tracking ID copied to clipboard!'))
        .catch(() => fallbackCopy(trackingId))
    } else {
      fallbackCopy(trackingId)
    }
  }

  const fallbackCopy = (text: string) => {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      toast.success('Tracking ID copied to clipboard!')
    } catch {
      toast.error('Failed to copy tracking ID')
    }
  }

  const onPlaceOrder = async (formData: CreateOrderInputType) => {
    setSubmitError('')
    try {
      const payload: CreateOrderInputType = {
        ...formData,
        customerPhone,
        customerEmail: formData.customerEmail?.trim() || undefined,
        items: items.map((i) => ({
          productId: i.productId,
          variantId: i.variantId,
          quantity: i.quantity,
        })),
      }
      const res = await apiPost<{
        orderNumber: string
        trackingId: string
        total: number
        isPreOrder?: boolean
      }>('orders', payload)
      if (formData.customerEmail?.trim()) {
        try {
          localStorage.setItem('buddys_customer_email', formData.customerEmail.trim())
        } catch {
          /* ignore */
        }
      }
      setOrderResult({
        orderNumber: res.orderNumber,
        trackingId: res.trackingId,
        total: res.total,
        isPreOrder: res.isPreOrder ?? items.some((i) => i.isPreOrder),
      })
      clearCart()
      setStep('success')
    } catch (err: unknown) {
      setSubmitError((err as Error).message || 'Failed to place order.')
    }
  }

  return (
    <StorefrontLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Continue shopping
        </Link>

        <div className="glass-luxury-card p-6 sm:p-8 space-y-6">
          <div className="flex items-center gap-2 text-xs font-mono text-[var(--text-faint)]">
            <span className={step === 'shipping' ? 'text-[var(--gold-main)] font-bold' : ''}>1. Delivery</span>
            <span>→</span>
            <span className={step === 'success' ? 'text-[var(--gold-main)] font-bold' : ''}>2. Confirm</span>
          </div>

          {step === 'shipping' && (
            <form onSubmit={handleSubmit(onPlaceOrder)} className="space-y-5">
              <div>
                <h1 className="text-xl font-title font-bold">Delivery Details</h1>
                <p className="text-xs text-emerald-600 font-mono mt-1">
                  Signed in as +91 {customerPhone}
                </p>
              </div>

              {savedAddresses.length > 0 && (
                <div className="space-y-3 pt-1 border-b border-[var(--border)] pb-4">
                  <div className="flex items-center justify-between">
                    <label className="form-label flex items-center gap-1.5 font-bold text-xs">
                      <MapPin className="w-3.5 h-3.5 text-[var(--gold-main)]" /> Select Saved Address
                    </label>
                    <button
                      type="button"
                      onClick={handleAddNewAddress}
                      className={`text-xs font-semibold hover:underline ${
                        selectedAddressId === 'new'
                          ? 'text-[var(--gold-main)] font-bold'
                          : 'text-[var(--gold-main)]/80'
                      }`}
                    >
                      + Add New Address
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {savedAddresses.map((addr) => {
                      const isSelected = selectedAddressId === addr.id
                      return (
                        <button
                          key={addr.id}
                          type="button"
                          onClick={() => selectSavedAddress(addr)}
                          className={`text-left p-3 rounded-xl border text-xs transition relative flex flex-col justify-between ${
                            isSelected
                              ? 'border-[var(--gold-main)] bg-[var(--gold-soft-bg)] ring-1 ring-[var(--gold-main)]'
                              : 'border-[var(--border)] hover:border-[var(--gold-main)]/50 bg-[var(--bg-surface-2)]/50'
                          }`}
                        >
                          <div>
                            <div className="flex items-center justify-between font-bold text-[var(--text-primary)]">
                              <span>{addr.name}</span>
                              {isSelected && (
                                <span className="text-[10px] text-[var(--gold-main)] bg-[var(--gold-main)]/15 px-1.5 py-0.5 rounded font-mono font-bold">
                                  Selected
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-[var(--text-muted)] line-clamp-2 mt-1">
                              {addr.shippingAddressLine1}
                              {addr.shippingAddressLine2 ? `, ${addr.shippingAddressLine2}` : ''}, {addr.shippingCity} – {addr.shippingPincode}
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {submitError && (
                <div className="alert alert-error text-xs">{submitError}</div>
              )}

              {errors.items && (
                <div className="alert alert-error text-xs">{errors.items.message}</div>
              )}

              <input type="hidden" {...register('customerPhone')} />

              <div className="form-group">
                <label className="form-label">Full name *</label>
                <input {...register('customerName')} className="form-input" placeholder="Your full name" aria-invalid={!!errors.customerName} />
                {errors.customerName && <span className="form-error">{errors.customerName.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Email (optional)</label>
                <input {...register('customerEmail')} type="email" className="form-input" placeholder="for invoice PDF" />
                {errors.customerEmail && <span className="form-error">{errors.customerEmail.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Address line 1 *</label>
                <input {...register('shippingAddressLine1')} className="form-input" placeholder="Door no, street" />
                {errors.shippingAddressLine1 && <span className="form-error">{errors.shippingAddressLine1.message}</span>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">City *</label>
                  <input {...register('shippingCity')} className="form-input" />
                  {errors.shippingCity && <span className="form-error">{errors.shippingCity.message}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Pincode *</label>
                  <input
                    {...register('shippingPincode')}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '')
                    }}
                    className="form-input font-mono"
                    placeholder="6-digit pincode"
                  />
                  {errors.shippingPincode && <span className="form-error">{errors.shippingPincode.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">State</label>
                <input {...register('shippingState')} className="form-input" />
              </div>

              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <div className="form-input flex items-center justify-between bg-[var(--bg-surface-2)] text-xs font-semibold">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Cash on Delivery (COD)
                  </span>
                  <span className="text-[10px] uppercase font-mono text-[var(--gold-main)] bg-[var(--gold-main)]/10 px-2 py-0.5 rounded border border-[var(--gold-main)]/20">
                    COD Only
                  </span>
                </div>
                <input type="hidden" {...register('paymentMethod')} value="cod" />
              </div>

              <div className="rounded-xl border border-[var(--border)] p-4 text-xs space-y-1 bg-[var(--bg-surface-2)]">
                <div className="flex justify-between"><span>Subtotal ({items.length} items)</span><span>{formatCurrency(subtotal)}</span></div>
                {gstOn && (
                  <div className="flex justify-between text-[var(--text-muted)]"><span>CGST + SGST</span><span>{formatCurrency(taxCgst + taxSgst)}</span></div>
                )}
                <div className="flex justify-between font-bold text-[var(--gold-main)] pt-2 border-t border-[var(--border)] text-sm">
                  <span>Total</span><span>{formatCurrency(total)}</span>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="btn-gold w-full py-3.5 text-sm flex items-center justify-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                {isSubmitting ? 'Placing order…' : 'Place order'}
              </button>
            </form>
          )}

          {step === 'success' && orderResult && (
            <div className="text-center space-y-5 py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto text-emerald-500">
                <CheckCircle className="w-8 h-8" />
              </div>
              <h1 className="text-2xl font-title font-bold">
                {orderResult.isPreOrder ? 'Pre-order confirmed!' : 'Order confirmed!'}
              </h1>
              <p className="text-xs text-[var(--text-muted)]">
                {orderResult.isPreOrder
                  ? "We'll craft your piece and update you when it ships."
                  : 'Your order has been placed. We will confirm and dispatch shortly.'}
              </p>
              <div className="rounded-xl border border-[var(--border)] p-4 text-left text-xs space-y-2">
                <div className="flex justify-between"><span>Order</span><span className="font-mono font-bold">{orderResult.orderNumber}</span></div>
                <div className="flex justify-between items-center">
                  <span>Tracking ID</span>
                  <span className="font-mono text-[var(--gold-main)] font-bold flex items-center gap-1.5">
                    {orderResult.trackingId}
                    <button
                      type="button"
                      onClick={() => handleCopyTrackingId(orderResult.trackingId)}
                      className="p-1 hover:bg-[var(--gold-main)]/10 rounded transition"
                      title="Copy Tracking ID"
                    >
                      <Copy className="w-3.5 h-3.5 text-[var(--gold-main)]" />
                    </button>
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[var(--border)]">
                  <span>Total</span><span className="font-bold text-[var(--gold-main)]">{formatCurrency(orderResult.total)}</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/" className="btn-gold-outline flex-1 py-3 text-xs text-center">Back to store</Link>
                <Link
                  to="/account/orders"
                  className="btn-gold flex-1 py-3 text-xs text-center flex items-center justify-center gap-1"
                >
                  <ShoppingBag className="w-4 h-4" /> My orders
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </StorefrontLayout>
  )
}

